<template>
  <DropDown ref="dropdown" class="w-96" wrapper-class="w-full" @close="searchTerm = ''">
    <template #activator="{ open, isOpen }">
      <InputField
        id="partner-search"
        v-model="searchTerm"
        data-testid="product-search"
        :disabled="disabled"
        :placeholder="t('Reviews.agency.edit.partners.input.placeholder').toString()"
        @update:model-value="handleSearch(open, isOpen)"
      >
        <template #prepend>
          <SearchIcon class="lucide-icon-s text-subtlest" />
        </template>
        <template #append>
          <span @click.stop="open">
            <button class="mt-1.5 text-subtlest" :class="{ hidden: searchTerm.length === 0 }" @click="searchTerm = ''">
              <Loader2Icon v-if="loading" class="lucide-icon-s animate-spin" />
              <XIcon v-else class="lucide-icon-s" />
            </button>
          </span>
        </template>
      </InputField>
    </template>
    <template #default>
      <div class="max-h-80 overflow-x-clip overflow-y-auto">
        <div v-if="loadingState" class="animate-pulse">
          <div v-for="i in 5" :key="`skeleton-product-${i}`" class="flex items-center gap-x-1 px-3 py-2.5">
            <div class="h-8 w-8 bg-surface-elevation-4" />
            <div class="h-4 w-full bg-surface-elevation-4" />
          </div>
        </div>
        <div v-else-if="products.length > 0">
          <div
            v-for="product in products"
            :key="product.id"
            class="flex cursor-pointer items-center gap-x-1 px-3 py-2.5 hover:bg-surface-elevation-2"
            :data-testid="`agency-partner-search-result-${product.id}`"
            @click="handleSelected(product)"
          >
            <ProductLogo class="min-w-8" :logo-url="product.logo?.url" size="small" />
            <span class="overflow-x-hidden overflow-ellipsis">{{ product.title }}</span>
          </div>
        </div>
        <div v-else class="px-3 py-2.5 text-subtlest">
          {{ t('Reviews.agency.edit.partners.input.notFound') }}
        </div>
      </div>
    </template>
  </DropDown>
</template>

<script setup lang="ts">
import { DropDown, InputField } from '@ramp106/omrjs-core-ui'
import { Loader2Icon, SearchIcon, XIcon } from '@ramp106/omrjs-core-ui/icons'
import { ProductLogo } from '@ramp106/omrjs-reviews-ui'
import { debounce } from 'lodash-es'
import { computed, ref, useTemplateRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import type { ProductBasicDataFragment } from '@/gql/reviews'

const props = withDefaults(
  defineProps<{
    products: ProductBasicDataFragment[]
    loading: boolean
    disabled?: boolean
    minSearchChars?: number
  }>(),
  {
    disabled: false,
    minSearchChars: 3,
  },
)

const emit = defineEmits<{
  (e: 'search', value: string): void
  (e: 'selected', product: ProductBasicDataFragment): void
}>()

const { t } = useI18n()

const dropdown = useTemplateRef('dropdown')

const searchTerm = ref('')

const localLoadingState = ref(false)

const loadingState = computed(() => localLoadingState.value || props.loading)

const debouncedSearch = debounce((term: string) => {
  emit('search', term)
  localLoadingState.value = false
}, 300)

function handleSearch(toggleDropdown: () => void, isOpen: boolean): void {
  if (searchTerm.value.length >= props.minSearchChars) {
    localLoadingState.value = true
    if (!isOpen) {
      toggleDropdown()
    }
  } else if (isOpen) {
    toggleDropdown()
  }
}

function handleSelected(product: ProductBasicDataFragment): void {
  emit('selected', product)
  searchTerm.value = ''
  dropdown.value?.close()
}

watch(searchTerm, (value) => {
  debouncedSearch(value)
})
</script>
